
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { Upload, Trash2, Eye, Check, Palette, X } from 'lucide-react';
import './AdminPanel.css';

const DEFAULT_THEME = {
    id: 'default',
    name: 'Default Theme',
    author: 'BPO Consultants',
    version: '1.0.0',
    description: 'The original BPO Consultants theme.',
    thumbnail: 'https://placehold.co/600x400/20222D/FFF?text=Default+Theme',
    cssVariables: {
        '--color-brand-primary': '#20222D',
        '--color-brand-accent': '#A2CB52',
        '--color-brand-light': '#B5D575',
        '--color-brand-lightest': '#ECF5DC',
    },
    customCss: ''
};

const ThemePreviewModal = ({ theme, onClose }) => {
    if (!theme) return null;

    const colors = theme.colors || {};
    const primary = colors.primary || '#20222D';
    const secondary = colors.secondary || '#1F2937';
    const bg = colors.background || '#FFFFFF';
    const text = colors.text || '#000000';
    const cardBg = colors['card-bg'] || '#FFFFFF';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative">
                <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-[var(--surface2)] rounded-full text-[var(--text2)] hover:text-white hover:bg-[var(--accent3)] transition-colors z-10">
                    <X size={20} />
                </button>

                <div className="p-6 border-b border-[var(--border)]">
                    <h2 className="text-2xl font-bold text-[var(--text)]">Theme Preview: <span style={{ color: primary }}>{theme.name}</span></h2>
                    <p className="text-[var(--muted)] text-sm mt-1">This is a simulation of how the theme colors might look.</p>
                </div>

                <div className="p-6 space-y-8" style={{ backgroundColor: bg, color: text, fontFamily: theme.fontFamily }}>
                    {/* Fake Header */}
                    <header className="flex justify-between items-center p-4 rounded-lg shadow-sm" style={{ backgroundColor: colors['nav-bg'] || '#fff' }}>
                        <div className="font-bold text-xl" style={{ color: primary }}>Logo</div>
                        <nav className="hidden md:flex gap-4 text-sm font-medium opacity-80">
                            <span>Home</span>
                            <span>About</span>
                            <span>Services</span>
                            <span>Contact</span>
                        </nav>
                        <button className="px-4 py-2 rounded-md font-bold text-sm text-white" style={{ backgroundColor: primary }}>
                            Get Started
                        </button>
                    </header>

                    {/* Fake Hero */}
                    <div className="text-center py-10">
                        <h1 className="text-4xl font-extrabold mb-4" style={{ color: text }}>Big Catchy Headline</h1>
                        <p className="max-w-xl mx-auto mb-6 opacity-70">Experience the difference with our premium services tailored just for you.</p>
                        <div className="flex gap-4 justify-center">
                            <button className="px-6 py-3 rounded-md font-bold text-white shadow-lg" style={{ backgroundColor: primary }}>Primary Action</button>
                            <button className="px-6 py-3 rounded-md font-bold border" style={{ borderColor: primary, color: primary }}>Secondary</button>
                        </div>
                    </div>

                    {/* Fake Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="p-6 rounded-xl shadow-sm border" style={{ backgroundColor: cardBg, borderColor: colors.border || '#e5e7eb' }}>
                                <div className="w-10 h-10 rounded-full mb-4 flex items-center justify-center text-white" style={{ backgroundColor: i === 2 ? secondary : primary }}>
                                    {i}
                                </div>
                                <h3 className="font-bold text-lg mb-2" style={{ color: text }}>Feature {i}</h3>
                                <p className="text-sm opacity-60">This feature description demonstrates the typography and contrast of the selected theme.</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-4 border-t border-[var(--border)] bg-[var(--surface2)] flex justify-end gap-3">
                    <button onClick={onClose} className="btn btn-ghost">Close Preview</button>
                </div>
            </div>
        </div>
    );
};

const ThemeManager = () => {
    const [themes, setThemes] = useState([]);
    const [activeThemeId, setActiveThemeId] = useState('default');
    const [loading, setLoading] = useState(true);

    const [previewTheme, setPreviewTheme] = useState(null);
    const [message, setMessage] = useState('');

    const handleImportTheme = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const themeData = JSON.parse(event.target.result);
                if (!themeData.id || !themeData.colors) {
                    alert("Invalid theme file.");
                    return;
                }
                const newThemes = [...themes, themeData];
                setThemes(newThemes);
                localStorage.setItem('available_themes', JSON.stringify(newThemes));
                alert("Theme imported successfully!");
            } catch (error) {
                console.error("Error parsing theme file:", error);
                alert("Error parsing theme file.");
            }
        };
        reader.readAsText(file);
    };

    // SEED THEMES
    const seedThemes = () => {
        if (!window.confirm("Add sample themes (Dark Mode & Ocean Blue)?")) return;

        const darkTheme = {
            id: 'theme_dark_mode',
            name: 'Dark Mode',
            colors: {
                primary: '#A2CB52', // Keep brand green
                secondary: '#1A1D24', // Darker gray
                accent: '#FFFFFF', // White text
                background: '#0B0D15', // Very dark bg
                text: '#E5E7EB', // Light gray text
                'text-secondary': '#9CA3AF',
                'nav-bg': '#11131A',
                'card-bg': '#1A1D24',
                'border': '#2D3748'
            },
            borderRadius: '12px',
            fontFamily: 'Inter, sans-serif'
        };

        const oceanTheme = {
            id: 'theme_ocean_blue',
            name: 'Ocean Blue',
            colors: {
                primary: '#0EA5E9', // Sky blue
                secondary: '#0284C7', // Darker blue
                accent: '#38BDF8', // Light blue
                background: '#F0F9FF', // Very light blue bg
                text: '#0C4A6E', // Dark blue text
                'text-secondary': '#64748B',
                'nav-bg': '#FFFFFF',
                'card-bg': '#FFFFFF',
                'border': '#BAE6FD'
            },
            borderRadius: '16px',
            fontFamily: 'Outfit, sans-serif'
        };

        const newThemes = [...themes];

        // Add if not exists
        if (!newThemes.find(t => t.id === darkTheme.id)) newThemes.push(darkTheme);
        if (!newThemes.find(t => t.id === oceanTheme.id)) newThemes.push(oceanTheme);

        setThemes(newThemes);
        localStorage.setItem('available_themes', JSON.stringify(newThemes));
        alert("New themes added! You can now activate them.");
    };

    useEffect(() => {
        fetchThemes();
    }, []);

    const fetchThemes = async () => {
        setLoading(true);
        try {
            // 1. Fetch Installed Themes
            const querySnapshot = await getDocs(collection(db, 'themes'));
            const themesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Ensure Default Theme is always present in the list (even if not in DB)
            const allThemes = [DEFAULT_THEME, ...themesData.filter(t => t.id !== 'default')];
            setThemes(allThemes);

            // 2. Fetch Active Theme Settings
            // const settingsSnap = await getDocs(collection(db, 'settings')); // Unused
            // We assume 'design' doc exists, if not we default
            // Actually let's fetch specific doc
            // const designDoc = await getDoc(doc(db, 'settings', 'design')); 
            // Better to just depend on what CustomStyles uses.
            // For now, let's fetch the 'design' doc to see activeThemeId
            // We can do this in a separate call or just rely on state if we sync it.

            // Let's implement active state fetching
            const designRef = doc(db, 'settings', 'design');
            const { getDoc } = await import('firebase/firestore');
            const designSnap = await getDoc(designRef);

            if (designSnap.exists() && designSnap.data().activeThemeId) {
                setActiveThemeId(designSnap.data().activeThemeId);
            }

        } catch (error) {
            console.error("Error fetching themes:", error);
            setMessage('Error loading themes.');
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = async (theme) => {
        if (!window.confirm(`Activate ${theme.name}? This will change the site's design immediately.`)) return;

        setLoading(true);
        try {
            // 1. Update Active Theme ID
            await setDoc(doc(db, 'settings', 'design'), {
                activeThemeId: theme.id,
                // We also Save the CSS to customCss field for backward compatibility 
                // OR we update CustomStyles to read theme. 
                // For safety, let's inject the theme's CSS variables + customCSS into the 'customCss' field
                // This ensures CustomStyles works even if we don't update it yet.
                customCss: generateThemeCss(theme)

            }, { merge: true });

            setActiveThemeId(theme.id);
            setMessage(`Theme "${theme.name}" activated successfully!`);
        } catch (error) {
            console.error("Error activating theme:", error);
            setMessage("Failed to activate theme.");
        } finally {
            setLoading(false);
        }
    };

    const generateThemeCss = (theme) => {
        let css = '/* Active Theme: ' + theme.name + ' */\n:root {\n';
        if (theme.cssVariables) {
            Object.entries(theme.cssVariables).forEach(([key, value]) => {
                css += `  ${key}: ${value};\n`;
            });
        }
        css += '}\n';
        if (theme.customCss) {
            css += '\n' + theme.customCss;
        }
        return css;
    };

    const handleDelete = async (themeId) => {
        if (themeId === 'default') {
            alert("Cannot delete the default theme.");
            return;
        }
        if (themeId === activeThemeId) {
            alert("Cannot delete the active theme. Activate another theme first.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this theme? This cannot be undone.")) return;

        try {
            await deleteDoc(doc(db, 'themes', themeId));
            setMessage("Theme deleted.");
            fetchThemes(); // Refresh list
        } catch (error) {
            console.error("Error deleting theme:", error);
            setMessage("Failed to delete theme.");
        }
    };



    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Look & Feel</div>
                    <h1>Theme Manager <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <button className="btn btn-ghost" onClick={seedThemes}>
                        <Palette size={14} /> Seed Themes
                    </button>
                    <label className="btn btn-primary cursor-pointer">
                        <Upload size={14} /> Install Theme
                        <input type="file" accept=".json" className="hidden" onChange={handleImportTheme} />
                    </label>
                </div>
            </div>

            {message && (
                <div className={`mb-6 text-sm px-4 py-3 rounded-md border ${message.includes('Error') || message.includes('Failed') ? 'bg-red-900/20 border-red-900/50 text-red-200' : 'bg-green-900/20 border-green-900/50 text-green-200'}`}>
                    {message}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {themes.map(theme => (
                    <div key={theme.id} className={`card transition-all ${activeThemeId === theme.id ? 'ring-2 ring-[var(--accent)] border-[var(--accent)]' : 'hover:border-[var(--text2)]'}`}>
                        {/* Thumbnail */}
                        <div className="h-40 bg-[var(--surface2)] relative group overflow-hidden border-b border-[var(--border)]">
                            {theme.thumbnail ? (
                                <img src={theme.thumbnail} alt={theme.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-[var(--muted)]">
                                    <span className="text-4xl font-bold opacity-20">THEME</span>
                                </div>
                            )}

                            {/* Overlay Actions */}
                            <div className="absolute inset-0 bg-black/60 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 gap-2">
                                <button
                                    onClick={() => setPreviewTheme(theme)}
                                    className="p-3 bg-[var(--surface)] border border-[var(--border)] rounded-full text-[var(--text)] hover:text-[var(--accent)] shadow-lg transform hover:scale-110 transition-all"
                                    title="Preview Theme"
                                >
                                    <Eye size={20} />
                                </button>
                            </div>

                            {activeThemeId === theme.id && (
                                <div className="absolute top-2 right-2 bg-[var(--accent)] text-black text-xs font-bold px-2 py-1 rounded-full flex items-center shadow-sm">
                                    <Check size={10} className="mr-1" /> Active
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="p-5">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="font-bold text-[var(--text)]">{theme.name}</h3>
                                    <p className="text-xs text-[var(--muted)]">v{theme.version} by {theme.author}</p>
                                </div>
                            </div>

                            <p className="text-sm text-[var(--text2)] line-clamp-2 mb-4 h-10">
                                {theme.description || 'No description provided.'}
                            </p>

                            <div className="flex items-center justify-between pt-4 border-t border-[var(--border)]">
                                {activeThemeId === theme.id ? (
                                    <span className="text-sm text-[var(--success)] font-medium flex items-center">
                                        <Check size={14} className="mr-1" /> Currently Active
                                    </span>
                                ) : (
                                    <button
                                        onClick={() => handleActivate(theme)}
                                        disabled={loading}
                                        className="text-sm font-medium text-[var(--accent)] hover:text-white"
                                    >
                                        Activate
                                    </button>
                                )}

                                {theme.id !== 'default' && (
                                    <button
                                        onClick={() => handleDelete(theme.id)}
                                        className="text-[var(--muted)] hover:text-[var(--accent3)] transition-colors"
                                        title="Delete Theme"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {themes.length === 0 && !loading && (
                <div className="text-center py-12">
                    <p className="text-[var(--muted)]">No themes found.</p>
                </div>
            )}

            <ThemePreviewModal theme={previewTheme} onClose={() => setPreviewTheme(null)} />
        </div>
    );
};

export default ThemeManager;
